/**
 * RPC multiplexer — the browser twin of `crates/rpc/src/client.rs` +
 * `crates/rpc/src/server.rs`.
 *
 * Framing is NDJSON of compact envelopes, carried as the payload of a device
 * `rpc` frame (or as WebSocket text on a direct connection):
 *   client → server: `{id, method, params}` to invoke, `{id, cancel: true}` to stop;
 *   server → client: `{id, ok}` / `{id, err}` unary, `{id, item}`* then `{id, done: true}` stream.
 *
 * A transport hands us *text*, which may batch several envelopes separated by
 * newlines. Every envelope is routed independently and malformed input is
 * dropped rather than thrown — one bad frame must not wedge the whole session.
 *
 * Cancellation is explicit (`{id, cancel: true}`) because dropping a stream here
 * cannot tell the host to stop; the engine keeps producing frames for an id
 * nobody reads. Watches must be cancelled on navigation and before resubscribe.
 */

export interface RpcTransport {
  /** Send one client frame. */
  send(frame: string): void;
  /** Whether the transport can currently accept frames. */
  readonly open: boolean;
}

export class RpcError extends Error {
  constructor(
    message: string,
    readonly kind: "failed" | "timeout" | "closed" | "cancelled" = "failed"
  ) {
    super(message);
    this.name = "RpcError";
  }
}

/** Default unary timeout — matches the engine's own 30s default budget. */
export const DEFAULT_CALL_TIMEOUT_MS = 30_000;

export interface SubscribeHandlers<T = unknown> {
  /** Every stream item, in arrival order. */
  onItem: (item: T) => void;
  /** The host finished the stream cleanly. */
  onDone?: () => void;
  /** The host rejected or aborted the stream. */
  onError?: (error: RpcError) => void;
  /** Resubscribe trigger: the consumer's copy diverged and needs a reset. */
  onDesync?: (reason: string) => void;
}

export interface Subscription {
  readonly id: number;
  cancel(): void;
}

interface PendingCall {
  kind: "call";
  resolve: (value: unknown) => void;
  reject: (error: RpcError) => void;
  timer: ReturnType<typeof setTimeout> | undefined;
}

interface PendingStream {
  kind: "stream";
  handlers: SubscribeHandlers<unknown>;
  /** Fires when the host never acknowledged a stream that should have. */
  timer: ReturnType<typeof setTimeout> | undefined;
  cancelled: boolean;
}

type Pending = PendingCall | PendingStream;

export interface RpcClientOptions {
  /** Injectable for tests. */
  setTimeout?: typeof setTimeout;
  clearTimeout?: typeof clearTimeout;
  /** Called for malformed or unroutable envelopes instead of throwing. */
  onProtocolWarning?: (message: string) => void;
}

export class RpcClient {
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;
  private closedReason: string | undefined;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;
  private readonly warn: (message: string) => void;

  constructor(
    private readonly transport: RpcTransport,
    options: RpcClientOptions = {}
  ) {
    // Wrapped, not stored bare. Two reasons: `setTimeout` called as
    // `this.setTimeoutFn(...)` would receive the `RpcClient` as its receiver
    // (Safari throws "Illegal invocation" for that), and an injected timer
    // would be called with a receiver it never expected either. Calling through
    // a local binding detaches both.
    const setT = options.setTimeout ?? setTimeout;
    const clearT = options.clearTimeout ?? clearTimeout;
    this.setTimeoutFn = (fn, ms) => setT(fn, ms);
    this.clearTimeoutFn = (handle) => clearT(handle);
    this.warn = options.onProtocolWarning ?? (() => {});
  }

  get pendingCount(): number {
    return this.pending.size;
  }

  get isClosed(): boolean {
    return this.closedReason !== undefined;
  }

  /**
   * Unary call. `timeoutMs: 0` disables the timeout (used for methods the engine
   * itself budgets at many minutes, such as harness installs).
   */
  call<T = unknown>(
    method: string,
    params: unknown = {},
    options: { timeoutMs?: number } = {}
  ): Promise<T> {
    if (this.closedReason !== undefined) {
      return Promise.reject(new RpcError(this.closedReason, "closed"));
    }
    const id = this.nextId++;
    const timeoutMs = options.timeoutMs ?? DEFAULT_CALL_TIMEOUT_MS;
    return new Promise<T>((resolve, reject) => {
      const entry: PendingCall = {
        kind: "call",
        resolve: resolve as (value: unknown) => void,
        reject,
        timer: undefined
      };
      if (timeoutMs > 0) {
        entry.timer = this.setTimeoutFn(() => {
          if (this.pending.get(id) !== entry) return;
          this.pending.delete(id);
          // Tell the host to stop working on it; the reply may still land and
          // will be dropped as unroutable, which is the intended behavior.
          this.sendFrame({ id, cancel: true });
          reject(new RpcError(`${method}: timed out after ${timeoutMs}ms`, "timeout"));
        }, timeoutMs);
      }
      this.pending.set(id, entry);
      this.sendFrame({ id, method, params });
    });
  }

  /**
   * Streaming subscribe. `onItem` runs synchronously on the transport's message
   * callback, so a slow consumer applies backpressure by blocking the socket —
   * the same shape the Rust client gets from its bounded stream queue.
   */
  subscribe<T = unknown>(
    method: string,
    params: unknown = {},
    handlers: SubscribeHandlers<T>,
    options: { timeoutMs?: number } = {}
  ): Subscription {
    if (this.closedReason !== undefined) {
      throw new RpcError(this.closedReason, "closed");
    }
    const id = this.nextId++;
    const timeoutMs = options.timeoutMs ?? DEFAULT_CALL_TIMEOUT_MS;
    const entry: PendingStream = {
      kind: "stream",
      handlers: handlers as SubscribeHandlers<unknown>,
      timer: undefined,
      cancelled: false
    };
    if (timeoutMs > 0) {
      // A watch that never produces its first snapshot means the host side
      // never started (bad params, unknown chat). Without this the UI would sit
      // on an empty screen forever.
      entry.timer = this.setTimeoutFn(() => {
        if (this.pending.get(id) !== entry || entry.cancelled) return;
        this.pending.delete(id);
        this.sendFrame({ id, cancel: true });
        handlers.onError?.(new RpcError(`${method}: no stream data within ${timeoutMs}ms`, "timeout"));
      }, timeoutMs);
    }
    this.pending.set(id, entry);
    this.sendFrame({ id, method, params });
    return { id, cancel: () => this.cancel(id) };
  }

  /** Cancel a request/stream and forget it. Idempotent. */
  cancel(id: number): void {
    const entry = this.pending.get(id);
    if (!entry) return;
    this.pending.delete(id);
    if (entry.timer !== undefined) this.clearTimeoutFn(entry.timer);
    if (entry.kind === "stream") {
      entry.cancelled = true;
    } else {
      entry.reject(new RpcError("cancelled", "cancelled"));
    }
    this.sendFrame({ id, cancel: true });
  }

  /** Feed inbound text (one or more newline-separated envelopes). */
  handleText(text: string): void {
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (trimmed.length === 0) continue;
      let frame: unknown;
      try {
        frame = JSON.parse(trimmed);
      } catch {
        this.warn(`dropping malformed rpc frame: ${trimmed.slice(0, 200)}`);
        continue;
      }
      this.route(frame);
    }
  }

  /**
   * Fail everything in flight. Called on link loss; the caller is responsible
   * for resubscribing (the protocol has no resume token for RPC streams).
   * Recoverable: the same client is reused after the reconnect.
   */
  failAll(reason: string): void {
    this.rejectAll(reason);
  }

  private rejectAll(reason: string): void {
    const entries = [...this.pending.entries()];
    this.pending.clear();
    for (const [, entry] of entries) {
      if (entry.timer !== undefined) this.clearTimeoutFn(entry.timer);
      if (entry.kind === "call") {
        entry.reject(new RpcError(reason, "closed"));
      } else {
        entry.cancelled = true;
        entry.handlers.onError?.(new RpcError(reason, "closed"));
      }
    }
  }

  /** Terminal shutdown (sign-out, page teardown). */
  close(reason = "connection closed"): void {
    this.closedReason = reason;
    this.rejectAll(reason);
  }

  private route(frame: unknown): void {
    if (typeof frame !== "object" || frame === null) {
      this.warn("dropping non-object rpc frame");
      return;
    }
    const raw = frame as Record<string, unknown>;
    if (typeof raw.id !== "number" || !Number.isFinite(raw.id)) {
      this.warn("dropping rpc frame without a numeric id");
      return;
    }
    const entry = this.pending.get(raw.id);
    if (!entry) return; // late reply for a cancelled/timed-out request
    if (typeof raw.err === "string") {
      this.settle(raw.id, entry, new RpcError(raw.err, "failed"));
      return;
    }
    if (raw.ok !== undefined) {
      if (entry.kind === "call") {
        this.finish(raw.id, entry);
        entry.resolve(raw.ok);
      }
      // A stream's `ok` is the versioned readiness ack
      // (WatchCheckoutChangeRequest sends `{stream:true}`). Ordinary streams
      // never send one, so nothing to do beyond disarming the first-item timer.
      else if (entry.timer !== undefined) {
        this.clearTimeoutFn(entry.timer);
        entry.timer = undefined;
      }
      return;
    }
    if (raw.item !== undefined) {
      if (entry.kind !== "stream") {
        this.warn(`dropping item for non-stream request ${raw.id}`);
        return;
      }
      if (entry.timer !== undefined) {
        this.clearTimeoutFn(entry.timer);
        entry.timer = undefined;
      }
      try {
        entry.handlers.onItem(raw.item);
      } catch (error) {
        // A throwing consumer means its copy is unreliable; resubscribe for a
        // reset instead of letting the exception escape into the socket loop.
        entry.cancelled = true;
        this.pending.delete(raw.id);
        entry.handlers.onDesync?.(`item handler threw: ${String(error)}`);
      }
      return;
    }
    if (raw.done === true) {
      if (entry.kind !== "stream") {
        this.warn(`dropping done for non-stream request ${raw.id}`);
        return;
      }
      this.finish(raw.id, entry);
      entry.handlers.onDone?.();
      return;
    }
    this.warn(`dropping unrecognized rpc frame: ${JSON.stringify(raw).slice(0, 200)}`);
  }

  private settle(id: number, entry: Pending, error: RpcError): void {
    this.finish(id, entry);
    if (entry.kind === "call") entry.reject(error);
    else entry.handlers.onError?.(error);
  }

  private finish(id: number, entry: Pending): void {
    this.pending.delete(id);
    if (entry.timer !== undefined) this.clearTimeoutFn(entry.timer);
  }

  private sendFrame(frame: Record<string, unknown>): void {
    if (!this.transport.open) {
      this.warn(`dropping frame for closed transport: ${JSON.stringify(frame).slice(0, 120)}`);
      return;
    }
    this.transport.send(JSON.stringify(frame));
  }
}
